import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { courseDocumentExtraPagesApi, CourseDocumentExtraPageApi } from "../courseDocumentExtraPagesApi";

const QUERY_KEYS = {
  COURSE_DOCUMENT_EXTRA_PAGES: (courseId: number, courseDocumentId: number) =>
    ["course-documents", courseId, "documents", courseDocumentId, "extra-pages"],
};

export const useCourseDocumentExtraPages = (courseId: number, courseDocumentId: number) => {
  const { isLoading, data, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.COURSE_DOCUMENT_EXTRA_PAGES(courseId, courseDocumentId),
    queryFn: () => courseDocumentExtraPagesApi.fetch(courseId, courseDocumentId),
    enabled: !!courseId && !!courseDocumentId,
    refetchOnWindowFocus: false,
  });

  return {
    loading: isLoading,
    data: data?.data ?? [],
    error,
    refresh: refetch,
  };
};

export const useUploadExtraPage = (courseId: number, courseDocumentId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, order }: { file: File; order: number }) =>
      courseDocumentExtraPagesApi.upload(courseId, courseDocumentId, file, order),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COURSE_DOCUMENT_EXTRA_PAGES(courseId, courseDocumentId),
      });
    },
  });
};

export const useDeleteExtraPage = (courseId: number, courseDocumentId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pageId: number) =>
      courseDocumentExtraPagesApi.delete(courseId, courseDocumentId, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COURSE_DOCUMENT_EXTRA_PAGES(courseId, courseDocumentId),
      });
    },
  });
};

export const useReorderExtraPages = (courseId: number, courseDocumentId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pageOrders: Record<string, number>) =>
      courseDocumentExtraPagesApi.reorder(courseId, courseDocumentId, pageOrders),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COURSE_DOCUMENT_EXTRA_PAGES(courseId, courseDocumentId),
      });
    },
  });
};